<template>
  <div class="base-textarea form-group">
    <div
      class="px-0"
      :class="`col-${col}`">
      <b-form-textarea
        :id="id"
        ref="textarea"
        :class="fieldClass"
        :value="_value"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="computedRows"
        :max-rows="autoGrow ? null : maxRows"
        :no-resize="autoGrow"
        v-on="customListeners" />
    </div>
    <BaseErrorMessage :errors="errors" />
  </div>
</template>

<script>
import BaseErrorMessage from '@/components/parts/BaseErrorMessage.vue'

// bootstrap-vue が rows を丸める下限
const MIN_ROWS = 2

export default {
  name: 'BaseTextarea',
  components: {
    BaseErrorMessage
  },
  props: {
    id: {
      type: String,
      default: undefined
    },
    value: {
      type: [String, Number],
      default: null
    },
    errors: {
      type: Array,
      default: () => []
    },
    col: {
      type: [String, Number],
      default: 12
    },
    name: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    emptyStrToNull: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rows: {
      type: [Number, String],
      default: 5
    },
    fieldClass: {
      type: String,
      default: null
    },
    maxRows: {
      type: [Number, String],
      default: null
    },
    // 入力量に合わせて高さを伸ばす。bootstrap-vue の max-rows は最低2行として
    // 高さを計算するため1行始まりにできず、自前で高さを調整する。
    // 高さは常に内容から決まるので rows は使わない
    autoGrow: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // autoGrow のときは高さを内容から決めるので、rows は mounted までの
    // 初期描画にしか効かない。bootstrap-vue が丸める下限をそのまま渡す
    computedRows() {
      return this.autoGrow ? MIN_ROWS : this.rows
    },
    _value: {
      get() {
        return this.value
      },
      set(v) {
        return this.$emit('input', v)
      }
    },
    customListeners() {
      return {
        ...this.$listeners,
        input: this.onInput,
      }
    }
  },
  watch: {
    value() {
      this.$nextTick(this.resize)
    }
  },
  mounted() {
    this.resize()
    if (!this.autoGrow) return

    // 幅が変わると折り返し位置が変わるので測り直す
    this._resizeHandler = () => {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(this.resize, 150)
    }
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeDestroy() {
    if (!this._resizeHandler) return

    window.removeEventListener('resize', this._resizeHandler)
    clearTimeout(this._resizeTimer)
  },
  methods: {
    resize() {
      if (!this.autoGrow) return

      const el = this.$refs.textarea?.$el
      if (!el) return

      // scrollHeight は border を含まないので、高さを潰す前に border 分を測っておく
      const borderHeight = el.offsetHeight - el.clientHeight
      // rows による最低高さを無視して内容だけの高さを測るために一度潰す
      el.style.height = '0'
      el.style.height = `${el.scrollHeight + borderHeight}px`
    },
    onInput(input) {
      if (input !== '') {
        this.$emit('input', input)
        return
      }

      this.$emit('input', this.emptyStrToNull ? null : input)
    }
  }
}
</script>

<style lang="scss" scoped>
.base-textarea {
  width: 100%;
  margin-bottom: 0;
}
</style>